import React, { useEffect, useState } from 'react';
import './styles/Navbar.css';

const API =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_STRAPI_URL) ||
  process.env.REACT_APP_STRAPI_URL ||
  process.env.NEXT_PUBLIC_STRAPI_URL ||
  'http://localhost:1337';

function hrefFor(item) {
  const slug = item.page?.slug || item.slug;
  return slug ? (slug === 'startseite' ? '/' : `/${slug}`) : '#';
}

const params = new URLSearchParams();
params.append('sort[0]', 'order:asc');
params.append('populate', '*');
params.append('pagination[pageSize]', '100');

const MENU_URL = `${API}/api/menu-items?${params.toString()}`;

export default function Navbar() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    fetch(MENU_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`Strapi ${r.status}`);
        return r.json();
      })
      .then((json) => {
        const rows = json.data || [];

        const getParentId = (item) => item?.ParentMenuItem?.id ?? null;

        const normalized = rows.map((r) => ({
          ...r,
          _children: [],
        }));

        const byId = new Map(normalized.map((r) => [r.id, r]));
        const top = [];

        normalized.forEach((item) => {
          const pid = getParentId(item);

          if (pid && byId.has(pid)) {
            byId.get(pid)._children.push(item);
          } else {
            top.push(item);
          }
        });

        const byOrder = (a, b) =>
          (a.order ?? 9999) - (b.order ?? 9999) ||
          String(a.title || '').localeCompare(String(b.title || ''), 'de');

        const sortTree = (arr) => {
          arr.sort(byOrder);

          arr.forEach((item) => {
            sortTree(item._children);
            item.child_menu_items = item._children;
          });
        };

        sortTree(top);
        setItems(top);
      })
      .catch(console.error);
  }, []);

 useEffect(() => {
  let ticking = false;

  const SHRINK_AT = 90;
  const EXPAND_AT = 12;

  const updateScrolled = () => {
    const y = window.scrollY || window.pageYOffset || 0;

    setScrolled((prev) => {
      if (!prev && y > SHRINK_AT) return true;
      if (prev && y < EXPAND_AT) return false;
      return prev;
    });

    ticking = false;
  };

  const onScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(updateScrolled);
      ticking = true;
    }
  };

  updateScrolled();

  window.addEventListener('scroll', onScroll, { passive: true });

  return () => window.removeEventListener('scroll', onScroll);
}, []);

  useEffect(() => {
    const mm =
      typeof window !== 'undefined'
        ? window.matchMedia('(max-width: 960px)')
        : null;

    const update = () => setIsMobile(!!mm?.matches);

    update();
    mm?.addEventListener('change', update);

    return () => mm?.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 960) {
        setOpen(false);
        setExpanded(new Set());
      }
    };

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleExpanded = (id) => {
    setExpanded((prev) => (prev.has(id) ? new Set() : new Set([id])));
  };

  const closeMobileMenu = () => {
    setOpen(false);
    setExpanded(new Set());
  };

  const isAktuelles = (title) =>
    String(title || '').trim().toLowerCase() === 'aktuelles';
  const isLinktree = (title) =>
    String(title || '').trim().toLowerCase() === 'linktree';

  return (
    <nav
      className={`navbar ${scrolled ? 'is-scrolled' : ''}`}
      role="navigation"
      aria-label="Hauptnavigation"
      data-mobile={isMobile ? 'true' : 'false'}
    >
      <div className="nav-inner">
        <a className="brand" href="/" onClick={closeMobileMenu}>
          <img
            className="logo"
            src="/schullogo_transparent.png"
            alt="Rothenburgschule Logo"
          />
        </a>

        <button
          className={`nav-toggle ${open ? 'is-open' : ''}`}
          type="button"
          aria-label="Menü umschalten"
          aria-expanded={open ? 'true' : 'false'}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="nav-toggle-bar" />
        </button>

        <ul className={`menu-list ${open ? 'is-open' : ''}`}>
          {items.map((item) => {
            const hasChildren = item.child_menu_items?.length > 0;
            const isExpanded = expanded.has(item.id);
            const submenuId = `submenu-${item.id}`;

            return (
              <li
                key={item.id}
                className={`menu-item ${hasChildren ? 'has-children' : ''} ${
                  isExpanded ? 'expanded' : ''
                } ${isAktuelles(item.title) ? 'is-highlighted' : ''} ${
                  isLinktree(item.title) ? 'is-linktree' : ''
                }`}
              >
                <a
                  href={hasChildren ? undefined : hrefFor(item)}
                  className={`menu-link ${hasChildren ? 'is-parent-only' : ''}`}
                  {...(hasChildren
                    ? { 'aria-haspopup': 'menu', role: 'button', tabIndex: 0 }
                    : {})}
                  {...(hasChildren && isMobile
                    ? { 'aria-expanded': isExpanded ? 'true' : 'false' }
                    : {})}
                  onClick={(e) => {
                    if (hasChildren) {
                      e.preventDefault();

                      if (isMobile) {
                        toggleExpanded(item.id);
                      } else {
                        // Am Desktop öffnet nur Hover das Untermenü — ein Klick soll
                        // keinen Fokus-Zustand hinterlassen, der es offen hält.
                        e.currentTarget.blur();
                      }

                      return;
                    }

                    closeMobileMenu();
                  }}
                  onKeyDown={(e) => {
                    if (
                      hasChildren &&
                      isMobile &&
                      (e.key === 'Enter' || e.key === ' ')
                    ) {
                      e.preventDefault();
                      toggleExpanded(item.id);
                    }
                  }}
                >
                  <span className="label">{item.title}</span>

                  {isMobile && hasChildren && (
                    <span className="chev" aria-hidden="true" />
                  )}
                </a>

                {hasChildren && (
                  <ul id={submenuId} className="submenu-list" role="menu">
                    {item.child_menu_items.map((child) => (
                      <li key={child.id} className="submenu-item" role="none">
                        <a
                          href={hrefFor(child)}
                          className="submenu-link"
                          role="menuitem"
                          onClick={closeMobileMenu}
                        >
                          {child.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}